import { Byte, Encoder } from "@nuintun/qrcode";

export const RETAIL_QUOTE_ORDER_HOST = "order.ranchpower.com";
export const RETAIL_QUOTE_INERT_ORDER_URL = "https://order.ranchpower.com/s/preview-test-inert-order-link";
export const RETAIL_QUOTE_QR_ERROR_CORRECTION = "M" as const;
export const RETAIL_QUOTE_QR_QUIET_ZONE_MODULES = 4;
export const RETAIL_QUOTE_QR_MIN_SIZE_POINTS = 54;
export const RETAIL_QUOTE_QR_SIZE_POINTS = 62;

const RETAIL_QUOTE_ORDER_PATH = /^\/s\/([A-Za-z0-9_-]{22,64})$/;
const RETAIL_QUOTE_ORDER_CLAIM = /^#claim=([A-Za-z0-9_-]{43,64})$/;

export type RetailQuoteOrderCtaMode = "live" | "preview" | "test";

export interface RetailQuoteOrderCta {
  mode: RetailQuoteOrderCtaMode;
  url: string;
}

export type RetailQuoteOrderCtaChannel = "email" | "pdf" | "preview";

export interface RetailQuoteQrMatrix {
  errorCorrection: typeof RETAIL_QUOTE_QR_ERROR_CORRECTION;
  modules: Uint8Array;
  quietZone: number;
  size: number;
  totalSize: number;
  url: string;
  version: number;
}

/**
 * Accept only the deliberately narrow, first-party launcher URL. In
 * particular, query strings, credentials, ports, and customer data have no
 * place in an order link. Only the live-email boundary may admit the exact
 * private claim-fragment shape; PDF/QR generation always uses the public URL.
 */
export function retailQuoteOrderUrl(value: unknown, allowClaim = false) {
  const source = String(value ?? "").trim();
  let parsed: URL;
  try {
    parsed = new URL(source);
  } catch {
    throw new TypeError("Retail quote ordering links must be valid HTTPS URLs.");
  }
  if (
    parsed.protocol !== "https:"
    || parsed.hostname.toLowerCase() !== RETAIL_QUOTE_ORDER_HOST
    || parsed.port
    || parsed.username
    || parsed.password
    || parsed.search
    || (parsed.hash && (!allowClaim || !RETAIL_QUOTE_ORDER_CLAIM.test(parsed.hash)))
    || !RETAIL_QUOTE_ORDER_PATH.test(parsed.pathname)
  ) {
    throw new TypeError("Retail quote ordering links must use an opaque first-party start handle.");
  }
  return `https://${RETAIL_QUOTE_ORDER_HOST}${parsed.pathname}${parsed.hash}`;
}

export function normalizeRetailQuoteOrderCta(
  value: RetailQuoteOrderCta | null | undefined,
  channel: RetailQuoteOrderCtaChannel = "pdf"
) {
  if (value == null) return undefined;
  if (!(["live", "preview", "test"] as const).includes(value.mode)) {
    throw new TypeError("Retail quote ordering links require an explicit delivery mode.");
  }
  const allowClaim = channel === "email" && value.mode === "live";
  return { mode: value.mode, url: retailQuoteOrderUrl(value.url, allowClaim) } satisfies RetailQuoteOrderCta;
}

export function retailQuoteOrderCtaSafetyLabel(mode: RetailQuoteOrderCtaMode) {
  if (mode === "test") return "TEST ONLY - NO CUSTOMER, ACCOUNT, OR ORDER CHANGES";
  if (mode === "preview") return "PREVIEW ONLY - NO CUSTOMER, ACCOUNT, OR ORDER CHANGES";
  return "";
}

export function retailQuoteOrderDisplayUrl(value: unknown, allowClaim = false) {
  const parsed = new URL(retailQuoteOrderUrl(value, allowClaim));
  return `${parsed.host}${parsed.pathname}`;
}

/** Build one deterministic, undecorated QR matrix shared by SVG and PDF. */
export function encodeRetailQuoteOrderQr(value: unknown): RetailQuoteQrMatrix {
  const url = retailQuoteOrderUrl(value);
  const encoded = new Encoder({ level: RETAIL_QUOTE_QR_ERROR_CORRECTION }).encode(new Byte(url));
  const modules = new Uint8Array(encoded.size * encoded.size);
  for (let y = 0; y < encoded.size; y += 1) {
    for (let x = 0; x < encoded.size; x += 1) {
      modules[y * encoded.size + x] = encoded.get(x, y);
    }
  }
  return {
    errorCorrection: RETAIL_QUOTE_QR_ERROR_CORRECTION,
    modules,
    quietZone: RETAIL_QUOTE_QR_QUIET_ZONE_MODULES,
    size: encoded.size,
    totalSize: encoded.size + RETAIL_QUOTE_QR_QUIET_ZONE_MODULES * 2,
    url,
    version: encoded.version
  };
}

function escapeSvgAttribute(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Inline SVG for authenticated previews; customer email intentionally uses a button instead. */
export function retailQuoteOrderQrSvg(value: unknown, label = "Secure Ranch Power ordering link") {
  const matrix = encodeRetailQuoteOrderQr(value);
  const path: string[] = [];
  for (let y = 0; y < matrix.size; y += 1) {
    for (let x = 0; x < matrix.size; x += 1) {
      if (!matrix.modules[y * matrix.size + x]) continue;
      path.push(`M${x + matrix.quietZone} ${y + matrix.quietZone}h1v1h-1z`);
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${escapeSvgAttribute(label)}" viewBox="0 0 ${matrix.totalSize} ${matrix.totalSize}" shape-rendering="crispEdges"><rect width="100%" height="100%" fill="#fff"/><path d="${path.join("")}" fill="#17313f"/></svg>`;
}
